import { ShiftAuthRequestError } from '../auth/shift-auth.service';
import type { ShiftEmployeeDetailedReport, ShiftEmployeeSummaryReport } from '../../models/reports.types';
import type { ShiftPlannedReportPage, ShiftPlannedReportQuery, ShiftComparisonReportPage, ShiftComparisonReportQuery } from 'shared-models';
import type { ShiftTipReportPage,ShiftTipReportQuery } from 'shared-models';

const backendBaseUrl = process.env.REACT_APP_BACKEND_LOCATION ?? '/__shift_api__';

const requestReport = async <T>(path: string): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${backendBaseUrl}${path}`, { credentials: 'include' });
  } catch {
    throw new ShiftAuthRequestError('אין תקשורת לשרת.', 0, true, null);
  }

  const payload = (await response.json().catch(() => null)) as { message?: string | string[] } | null;
  if (!response.ok) {
    const rawMessage = payload?.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : (rawMessage ?? `HTTP ${response.status}`);
    throw new ShiftAuthRequestError(message, response.status, false, payload);
  }
  return payload as T;
};

class ShiftReportsService {
  async getTips(query:ShiftTipReportQuery):Promise<ShiftTipReportPage> {
    const parameters=new URLSearchParams({from:query.from,to:query.to,view:query.view ?? 'detail',offset:String(query.offset ?? 0),limit:String(query.limit ?? 50)});
    if (query.employeePk!==undefined) parameters.set('employeePk',String(query.employeePk));
    return requestReport(`/shift/schedule/reports/tips?${parameters}`);
  }
  async getComparison(query:ShiftComparisonReportQuery):Promise<ShiftComparisonReportPage> {
    const parameters = new URLSearchParams({from:query.from,to:query.to,offset:String(query.offset ?? 0),limit:String(query.limit ?? 50),exceptionsOnly:String(query.exceptionsOnly ?? false)});
    if (query.employeePk!==undefined) parameters.set('employeePk',String(query.employeePk));
    return requestReport(`/shift/schedule/reports/comparison?${parameters}`);
  }

  async getPlanned(query:ShiftPlannedReportQuery):Promise<ShiftPlannedReportPage> {
    const parameters = new URLSearchParams({from:query.from,to:query.to,source:query.source ?? 'draft',view:query.view ?? 'detail',
      offset:String(query.offset ?? 0),limit:String(query.limit ?? 50)});
    if (query.employeePk!==undefined) parameters.set('employeePk',String(query.employeePk));
    return requestReport(`/shift/schedule/reports/planned?${parameters}`);
  }

  async getDetailed(month: string): Promise<ShiftEmployeeDetailedReport> {
    return requestReport(`/shift/schedule/reports/detail?month=${encodeURIComponent(month)}`);
  }

  async getSummary(): Promise<ShiftEmployeeSummaryReport> {
    return requestReport('/shift/schedule/reports/summary');
  }
}

export const shiftReportsService = new ShiftReportsService();
